import { api } from "@/lib/apiClient";
import {
  resolveReferencePositions,
  type StoredNoteReference,
} from "@/projectOverview/notes/noteReferences";
import type { DraftNoteReference } from "@/projectOverview/notes/types";

/** Mitarbeiter (Übergabe/Einladung) – login = Mention-Ref-ID (i. d. R. E-Mail). */
export interface TodoPerson {
  login: string;
  display: string;
}

export interface TodoItem {
  id: number;
  text: string;
  references: StoredNoteReference[];
  created_by_login?: string | null;
  created_by_display?: string | null;
  assigned_login?: string | null;
  assigned_display?: string | null;
  /** Wiedervorlage-Datum (ISO, YYYY-MM-DD) oder null. */
  remind_at?: string | null;
  position: number;
  done_at?: string | null;
  done_by_display?: string | null;
  created_at?: string | null;
  participants: TodoPerson[];
  viewer_is_creator: boolean;
  /** True, wenn der aktuelle User per @ erwähnt wurde (und nicht selbst Ersteller ist). */
  viewer_is_mentioned?: boolean;
}

export interface TodoCreatePayload {
  text: string;
  references: DraftNoteReference[];
  remind_at?: string | null;
  assigned?: TodoPerson | null;
  participants?: TodoPerson[];
}

export interface TodoUpdatePayload {
  text?: string;
  references?: DraftNoteReference[];
  remind_at?: string | null;
  assigned?: TodoPerson | null;
  participants?: TodoPerson[];
}

export async function fetchTodos(): Promise<TodoItem[]> {
  const data = await api.get<{ items?: TodoItem[] }>("/todos");
  return Array.isArray(data?.items) ? data.items : [];
}

export async function createTodo(payload: TodoCreatePayload): Promise<TodoItem> {
  return api.post<TodoItem>("/todos", payload);
}

export async function updateTodo(
  id: number,
  payload: TodoUpdatePayload,
): Promise<TodoItem> {
  return api.patch<TodoItem>(`/todos/${id}`, payload);
}

export async function setTodoDone(id: number, done: boolean): Promise<TodoItem> {
  return api.post<TodoItem>(`/todos/${id}/done`, { done });
}

export async function reorderTodos(ids: number[]): Promise<void> {
  await api.post("/todos/reorder", { ids });
}

export async function deleteTodo(id: number): Promise<void> {
  await api.del(`/todos/${id}`);
}

/** Globale Vorschlagssuche für To-Do-Erwähnungen (ohne Auftragskontext). */
export function searchTodoReferences(
  kind: "orders" | "timesheets" | "dokumente" | "material",
  q: string,
  limit = 8,
): Promise<Array<{ id: string; label: string; subtitle?: string | null }>> {
  const params = new URLSearchParams({ kind, q, limit: String(limit) });
  return api.get(`/todos/reference-search?${params.toString()}`);
}

/** Lokales Datum als YYYY-MM-DD (keine UTC-Verschiebung um Mitternacht). */
export function todayIsoDate(): string {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const day = String(now.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

export type TodoDueState = "overdue" | "due-today" | "upcoming" | "none";

export function todoDueState(todo: Pick<TodoItem, "remind_at" | "done_at">): TodoDueState {
  if (todo.done_at || !todo.remind_at) return "none";
  const today = todayIsoDate();
  const remind = String(todo.remind_at).slice(0, 10);
  if (remind < today) return "overdue";
  if (remind === today) return "due-today";
  return "upcoming";
}

/** Vorlage für den Schnell-To-Do-Dialog (Text + Referenzen). */
export interface TodoDraft {
  text: string;
  references: DraftNoteReference[];
}

function projectReferenceToken(
  auftragId: number,
  auftragsnummer?: string | null,
): { token: string; reference: DraftNoteReference } {
  const label = String(auftragsnummer || "").trim() || `#${auftragId}`;
  const token = `Auftrag ${label}`;
  return {
    token,
    reference: {
      type: "auftrag",
      ref_id: String(auftragId),
      label,
      trigger: "#",
      token_text: token,
      start: 0,
      end: token.length,
    },
  };
}

/** Vorlage „Projekt auf Wiedervorlage“: klickbare Projekt-Referenz + Platz für Notiz. */
export function buildProjectTodoDraft(
  auftragId: number,
  auftragsnummer?: string | null,
): TodoDraft {
  const { token, reference } = projectReferenceToken(auftragId, auftragsnummer);
  return { text: `${token} – `, references: [reference] };
}

/** Vorlage aus einer Erwähnung: Notiztext + Referenzen, ergänzt um die Projekt-Referenz. */
export function buildMentionTodoDraft(mention: {
  note_text?: string | null;
  note_preview?: string | null;
  note_references?: StoredNoteReference[] | null;
  auftrag_id?: number | null;
  auftragsnummer?: string | null;
}): TodoDraft {
  const baseText = String(mention.note_text || mention.note_preview || "").trim();
  const baseRefs = resolveReferencePositions(baseText, mention.note_references || []);
  const auftragId = Number(mention.auftrag_id || 0);
  if (!auftragId) return { text: baseText, references: baseRefs };

  const alreadyLinked = baseRefs.some(
    (ref) => ref.type === "auftrag" && ref.ref_id === String(auftragId),
  );
  if (alreadyLinked) return { text: baseText, references: baseRefs };

  const { token, reference } = projectReferenceToken(auftragId, mention.auftragsnummer);
  const prefix = `${token}: `;
  return {
    text: `${prefix}${baseText}`,
    references: [
      reference,
      ...baseRefs.map((ref) => ({
        ...ref,
        start: ref.start + prefix.length,
        end: ref.end + prefix.length,
      })),
    ],
  };
}

export function formatRemindDate(value?: string | null): string {
  if (!value) return "";
  const parsed = new Date(`${String(value).slice(0, 10)}T00:00:00`);
  if (Number.isNaN(parsed.getTime())) return "";
  return parsed.toLocaleDateString("de-DE", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
}
